import { CountryCode } from '@constants'
import { PaymentServiceType } from '@dni-types/data-models/ecommerce-payment'
import type * as TapPay from '@dni-types/payment/tappay'
import { PAYMENT_ERROR_CODE, PaymentError } from './PaymentError'
import { type PaymentModule, type TapPayPaymentDetails } from './types'
import { isTappaySdkLoaded, loadTappayScript } from './utilities'

export class TappayApplePayModule implements PaymentModule<TapPayPaymentDetails> {
  isLoaded = false
  currency: TapPayPaymentDetails['currency'] = 'TWD'
  countryCode: TapPayPaymentDetails['countryCode'] = CountryCode.TW
  orderTotalAmount: TapPayPaymentDetails['orderTotalAmount'] = 0
  merchantIdentifier: TapPayPaymentDetails['merchantIdentifier'] = ''
  appEnv: TapPayPaymentDetails['appEnv'] = 'sandbox'
  appId: Required<TapPayPaymentDetails>['appId'] = ''
  appKey: Required<TapPayPaymentDetails>['appKey'] = ''
  displayItems: TapPayPaymentDetails['displayItems'] = []
  onCancel?: TapPayPaymentDetails['onCancel']

  prime: string | null = null
  paymentRequest: TapPay.PaymentRequest | null = null

  public loadScript = () => loadTappayScript().then(() => {
    this.isLoaded = true
  })

  public configurePayment(details: TapPayPaymentDetails) {
    console.debug('[Tappay Apple Pay] configurePayment details:', details)
    if (!isTappaySdkLoaded(window.TPDirect)) return
    if (!details.appId || !details.appKey) {
      throw new PaymentError('[Tappay Apple Pay] appId or appKey is required', PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.INVALID_APP_ID_OR_APP_KEY)
    }

    this.currency = details.currency
    this.countryCode = details.countryCode
    this.orderTotalAmount = details.orderTotalAmount
    this.merchantIdentifier = details.merchantIdentifier
    this.displayItems = details.displayItems
    this.appKey = details.appKey
    this.appId = details.appId
    this.appEnv = details.appEnv
    this.onCancel = details.onCancel
    this.paymentRequest = {
      supportedNetworks: ['AMEX', 'JCB', 'MASTERCARD', 'VISA'],
      supportedMethods: ['apple_pay'],
      displayItems: this.displayItems,
      total: {
        label: 'TapPay',
        amount: {
          currency: this.currency,
          value: this.orderTotalAmount,
        },
      },
    }

    try {
      window.TPDirect.setupSDK(this.appId, this.appKey, this.appEnv)
      window.TPDirect.paymentRequestApi.setupApplePay({
        merchantIdentifier: this.merchantIdentifier,
        countryCode: this.countryCode,
      })

      window.TPDirect.paymentRequestApi.setupPaymentRequest(this.paymentRequest, () => {
      // TODO: Should createApplePayToken after setupPaymentRequest
      })
    }
    catch (error) {
      throw new PaymentError(`[Tappay Apple Pay] Configuration failed: ${error}`, PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.CONFIGURATION_FAILED, error)
    }
  }

  public getPrime = (): Promise<string> => new Promise((resolve, reject) => {
    if (this.prime) {
      resolve(this.prime)
    }
    else {
      reject(new PaymentError('[Tappay Apple Pay] Payment Token not initialized', PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.INVALID_PRIME))
    }
  })

  /**
   * [Warning] Create ApplePaySession is only available on click event callback.
   * https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api/creating_an_apple_pay_session#overview
   */
  public createApplePayToken = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!isTappaySdkLoaded(window.TPDirect)) return
      try {
        window.TPDirect.paymentRequestApi.getPrime((result: { status: number, prime: string }) => {
          console.debug('[Tappay Apple Pay] getPrime result:', result)
          if (result.status !== 0) {
            this.onCancel?.()
            reject(new PaymentError('[Tappay Apple Pay] Failed to get prime', PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.INVALID_PRIME, result))
          }
          this.prime = result.prime
          resolve(result.prime)
        })
      }
      catch (error) {
        reject(new PaymentError(`[Tappay Apple Pay] Failed to get prime: ${error}`, PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.GET_PRIME_ERROR, error))
      }
    })
  }
}
